import { FAQ } from '@thurstech/commons/dist/entity/prop';
import _ from 'lodash';
import React from 'react';
import {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  FieldValues,
  FieldPath,
  PathValue,
  FieldPathByValue
} from 'react-hook-form';

import Input from '../components/input/Input';
import InputTextArea from '../components/input/InputTextArea';

interface FAQSectionProps<TFieldValues extends FieldValues> {
  register: UseFormRegister<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
  trigger: UseFormTrigger<TFieldValues>;
  name: FieldPathByValue<TFieldValues, FAQ[] | undefined>;
  title?: string;
}

const FAQSection = <TFieldValues extends FieldValues>({
  register,
  errors,
  setValue,
  watch,
  trigger,
  name,
  title = "FAQ's"
}: FAQSectionProps<TFieldValues>): JSX.Element => {
  const faqs = (watch(name) ?? []) as FAQ[];
  const faqsError = _.get(errors, name)?.message as string;

  const commonProps = {
    topLabel: true,
    required: true,
    register,
    errors
  };

  const handleAddFAQ = async () => {
    const isValid = await trigger(name);
    if (isValid) {
      setValue(name, [...faqs, { question: '', answer: '' }] as PathValue<TFieldValues, typeof name>);
    }
  };

  return (
    <section className="mb-8">
      <div>
        <div className="flex justify-between mb-4">
          <h3 className="text-2xl font-semibold">{title}</h3>
          <button
            onClick={handleAddFAQ}
            type="button"
            className="border-2 border-black bg-gray-200 rounded-md px-5 py-2"
          >
            Add FAQ
          </button>
        </div>
        {faqsError && <p className="text-red-600 text-sm font-semibold">{faqsError}</p>}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {faqs.map((faq, index) => (
          <div key={index} className="flex flex-col space-y-2">
            <Input<TFieldValues>
              label={`Question ${index + 1}`}
              name={`${name}.${index}.question` as FieldPath<TFieldValues>}
              type="text"
              placeholder={`Enter Question ${index + 1}`}
              {...commonProps}
            />
            <InputTextArea<TFieldValues>
              label={`Answer ${index + 1}`}
              name={`${name}.${index}.answer` as FieldPath<TFieldValues>}
              maxLength={500}
              placeholder={`Enter Answer ${index + 1}`}
              {...commonProps}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default FAQSection;
